import { PERSON_FIELDS, type PersonField } from './rules';
import { pickFirst, type PersonResult } from './people';

/** Fields we read from `Member` (search) and `MemberModelView` (getViewById). */
export interface MemberLike {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  cellNumber?: string | null;
  telephone?: string | null;
  altContact?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
}

export interface MemberDeps {
  getViewById: (id: number) => Promise<unknown>;
  searchMembers: (term: string, limit: number) => Promise<unknown>;
}

export interface MemberCache {
  byId: Map<number, Promise<MemberLike | null>>;
  bySearch: Map<string, Promise<MemberLike[]>>;
}

export const createMemberCache = (): MemberCache => ({ byId: new Map(), bySearch: new Map() });

/** A full page means more matches may exist, so uniqueness cannot be proven. */
export const NAME_SEARCH_LIMIT = 25;

const norm = (v: unknown) => String(v ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

/** The SDK may hand back the bare value or a `{ data }` wrapper — the Counselees tab handles both. */
const unwrap = (res: unknown): unknown =>
  res && typeof res === 'object' && !Array.isArray(res) && 'data' in res
    ? (res as { data: unknown }).data
    : res;

function search(term: string, deps: MemberDeps, cache: MemberCache): Promise<MemberLike[]> {
  const key = norm(term);
  let hit = cache.bySearch.get(key);
  if (!hit) {
    hit = deps.searchMembers(term, NAME_SEARCH_LIMIT)
      .then((res) => {
        const list = unwrap(res);
        return Array.isArray(list) ? (list as MemberLike[]) : [];
      })
      .catch((e) => { cache.bySearch.delete(key); throw e; });
    cache.bySearch.set(key, hit);
  }
  return hit;
}

/** Name route (risky): exactly one exact first+last match, and the search was not a full page. */
async function findByName(
  first: string, surname: string, deps: MemberDeps, cache: MemberCache,
): Promise<MemberLike | null> {
  let list = await search(`${first} ${surname}`, deps, cache);
  if (list.length === 0) list = await search(surname, deps, cache);
  if (list.length >= NAME_SEARCH_LIMIT) return null;

  const exact = list.filter(
    (m) => norm(m.firstName) === norm(first) && norm(m.lastName) === norm(surname),
  );
  return exact.length === 1 ? exact[0] : null;
}

/** Id route (safe) when the person has a member id, otherwise the name route. */
export function findMember(
  person: PersonResult, deps: MemberDeps, cache: MemberCache,
): Promise<MemberLike | null> {
  if (person.memberID != null) {
    const id = person.memberID;
    let hit = cache.byId.get(id);
    if (!hit) {
      hit = deps.getViewById(id)
        .then((res) => (unwrap(res) as MemberLike | null) ?? null)
        .catch((e) => { cache.byId.delete(id); throw e; });
      cache.byId.set(id, hit);
    }
    return hit;
  }
  const { firstName, surname } = person.values;
  if (!firstName || !surname) return Promise.resolve(null); // nothing to search by
  return findByName(firstName, surname, deps, cache);
}

/**
 * Fills ONLY the fields still empty, through the same missing rule. Never overwrites Source 2.
 * Returns how many fields were filled.
 */
export function applyMember(person: PersonResult, m: MemberLike): number {
  const from: Record<PersonField, unknown[]> = {
    firstName: [m.firstName],
    surname: [m.lastName],
    email: [m.email],
    phone: [m.cellNumber, m.telephone, m.altContact],
    gender: [m.gender],
    dateOfBirth: [m.dateOfBirth],
  };
  let filled = 0;
  for (const f of PERSON_FIELDS) {
    if (person.values[f] !== null) continue;
    const got = pickFirst(f, from[f]);
    if (got.value === null) continue;
    person.values[f] = got.value;
    person.from[f] = 3;
    delete person.issues[f];
    if (person.rawIssues) delete person.rawIssues[f];
    filled += 1;
  }
  return filled;
}
